import React, { useState, useEffect } from 'react';
import { Tv, Smartphone, Tablet, Monitor, Box, Usb, Settings, Play, ChevronDown, ChevronUp, Download, Globe, AlertCircle, Copy, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import api from '../services/api';
import TutorialsAssistant from '../components/Assistant/TutorialsAssistant';

const Tutorials = () => {
  const [expandedDevice, setExpandedDevice] = useState(null);
  const [dnsSettings, setDnsSettings] = useState({ server_url: '', smarters_domain: '' });
  const [userCredentials, setUserCredentials] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Charger les paramètres DNS et les credentials utilisateur
  useEffect(() => {
    const prev = document.title;
    document.title = 'Tutoriels et assistance | IPTV4KFR';
    // Statistique : ouverture de la page Tutoriels et assistance
    api.post('/assistant/page-event', { event: 'tutoriels_opened' }).catch(() => {});
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      // Récupérer les paramètres DNS publics
      try {
        const dnsResponse = await api.get('/admin/settings/dns/public');
        if (dnsResponse.data) {
          setDnsSettings({
            server_url: dnsResponse.data.server_url || '',
            smarters_domain: dnsResponse.data.smarters_domain || ''
          });
        }
      } catch (err) {
        // DNS settings not available - continue
      }

      // Vérifier si l'utilisateur est connecté et récupérer ses credentials
      const token = localStorage.getItem('auth_token');
      if (token) {
        setIsLoggedIn(true);
        try {
          const credsResponse = await api.get('/client/my-credentials');
          if (credsResponse.data && credsResponse.data.length > 0) {
            setUserCredentials(credsResponse.data[0]);
          }
        } catch (credErr) {
          // Credentials not available for this user
        }
      }
    };

    fetchData();
  }, []);

  // Générer le lien M3U complet
  const generateM3ULink = () => {
    // Utiliser l'URL serveur des credentials de l'utilisateur, ou le DNS global, ou un placeholder
    let serverUrl = dnsSettings.server_url || 'http://DNS-SERVEUR:PORT';
    
    if (isLoggedIn && userCredentials) {
      // Utiliser l'URL serveur des credentials de l'utilisateur si disponible
      if (userCredentials.server_url) {
        serverUrl = userCredentials.server_url;
      }
      return `${serverUrl}/get.php?username=${userCredentials.username}&password=${userCredentials.password}&type=m3u_plus&output=mpegts`;
    }
    
    return `${serverUrl}/get.php?username=VOTRE_USERNAME&password=VOTRE_PASSWORD&type=m3u_plus&output=mpegts`;
  };

  // Copier dans le presse-papier
  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const devices = [
    {
      id: 'android',
      icon: <Tv className="h-12 w-12" />,
      title: 'Android',
      subtitle: 'Android TV / Smart TV Android / Box Android / Clé HDMI / Smartphone / Tablette',
      color: '#00FF88',
      recommended: true,
      description: 'Solution universelle pour tous les appareils Android. La clé HDMI Android est incluse dans ce tutoriel.',
      app: 'GOLDEN OTT',
      advantages: [
        'Compatible tous appareils Android',
        'Installation rapide via Downloader',
        'Meilleure stabilité',
        'Interface intuitive'
      ],
      methods: [
        {
          title: '📥 Installation via DOWNLOADER (Méthode recommandée)',
          steps: [
            'Installer Downloader depuis le Play Store (ou l\'Amazon Appstore sur Fire TV)',
            'Ouvrir l\'application Downloader',
            { text: 'Ouvrir l\'onglet Téléchargements de notre site pour récupérer le code de l\'application :', downloadsLink: true },
            'Saisir dans Downloader le code affiché, puis télécharger et installer l\'application',
            'Ouvrir l\'application installée',
            'À l\'écran de connexion, entrer votre USERNAME et PASSWORD'
          ]
        }
      ],
      alternative: 'Les applications compatibles sont disponibles dans la rubrique Téléchargement.',
      videoAvailable: true
    },
    {
      id: 'samsung-lg-smarters',
      icon: <Tv className="h-12 w-12" />,
      title: 'Samsung / LG',
      subtitle: 'Application IPTV SMARTERS',
      color: '#FF6B35',
      description: 'Application gratuite disponible sur le store de votre Smart TV.',
      app: 'IPTV Smarters',
      steps: [
        'Accéder au Samsung Apps ou LG Content Store',
        'Rechercher "IPTV Smarters"',
        'Installer l\'application',
        'Ouvrir l\'application',
        'Choisir "Connexion Xtream Codes API"',
        'Entrer vos identifiants (Username, Password, URL Serveur)'
      ],
      dynamicServerUrl: true, // Flag pour afficher l'URL dynamique
      videoAvailable: true
    },
    {
      id: 'samsung-lg-ibo',
      icon: <Tv className="h-12 w-12" />,
      title: 'Samsung / LG',
      subtitle: 'Application IBO PLAYER',
      color: '#F7931E',
      description: 'Application premium avec interface optimisée pour Smart TV.',
      app: 'IBO Player',
      important: {
        text: 'Application payante indépendante de notre plateforme',
        price: '10€',
        details: 'Paiement unique • Licence valable à vie'
      },
      steps: [
        'Installer IBO Player depuis le store TV',
        'Ouvrir l\'application',
        'Noter l\'adresse MAC et le Device ID',
        { text: 'Se rendre sur :', link: 'https://iboplayer.com/device/login' },
        'Ajouter votre playlist M3U'
      ],
      dynamicM3U: true, // Flag pour afficher le lien M3U dynamique
      videoAvailable: true
    },
    {
      id: 'mag-enigma',
      icon: <Settings className="h-12 w-12" />,
      title: 'MAG / ENIGMA2',
      subtitle: 'Décodeurs spécialisés',
      color: '#00FFFF',
      description: 'Configuration pour les appareils MAG et récepteurs Enigma2.',
      sections: [
        {
          title: '📋 Configuration MAG',
          steps: [
            'Nous transmettre l\'adresse MAC de votre appareil',
            'Une fois activé, aller dans : Paramètres → Portail',
            { text: 'Dans URL Portail, entrer :', dynamicPortalUrl: true },
            'Redémarrer l\'appareil'
          ],
          warning: '⚠️ Bien ajouter /c/ à la fin de l\'URL serveur.'
        },
        {
          title: '📋 Configuration ENIGMA2',
          steps: [
            'Nous transmettre l\'adresse MAC',
            { text: 'Dans les paramètres IPTV de votre appareil, ajouter l\'URL serveur suivante :', dynamicPortalUrl: true },
            'Redémarrer l\'appareil'
          ],
          warning: '⚠️ L\'URL doit obligatoirement se terminer par /c/'
        }
      ],
      videoAvailable: true
    },
    {
      id: 'ios',
      icon: <Smartphone className="h-12 w-12" />,
      title: 'iOS',
      subtitle: 'iPhone / iPad',
      color: '#FF6B9D',
      description: 'Regardez vos contenus sur vos appareils Apple.',
      app: 'IPTV Smarters Lite',
      appNote: '(ou toute application compatible Xtream Codes)',
      steps: [
        'Ouvrir l\'App Store',
        'Installer "IPTV Smarters Lite"',
        'Ouvrir l\'application',
        'Sélectionner "Connexion Xtream Codes API"',
        'Entrer vos identifiants : Nom d\'utilisateur, Mot de passe, URL serveur (DNS principal)'
      ],
      videoAvailable: true
    }
  ];

  const compatibleApps = [
    { name: 'GOLDEN OTT', platform: 'Android' },
    { name: 'IPTV Smarters', platform: 'Samsung/LG/iOS' },
    { name: 'IBO Player', platform: 'Samsung/LG' },
    { name: 'TiviMate', platform: 'Android' },
    { name: 'Apps Xtream Codes', platform: 'Tous appareils' }
  ];

  const toggleDevice = (deviceId) => {
    setExpandedDevice(expandedDevice === deviceId ? null : deviceId);
  };

  const renderStep = (step, index) => {
    if (typeof step === 'string') {
      return (
        <li key={index} className="flex items-start space-x-3">
          <span className="flex-shrink-0 w-6 h-6 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold">
            {index + 1}
          </span>
          <span className="text-[#E8E8E8] text-sm">{step}</span>
        </li>
      );
    } else if (step.highlight) {
      return (
        <li key={index} className="flex items-start space-x-3">
          <span className="flex-shrink-0 w-6 h-6 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold">
            {index + 1}
          </span>
          <div className="text-[#E8E8E8] text-sm">
            {step.text}
            <div className="mt-2 bg-[#0A0A0B] border border-[#00FFFF] rounded-lg px-4 py-3 font-mono text-[#00FFFF] text-lg font-bold">
              {step.highlight}
            </div>
          </div>
        </li>
      );
    } else if (step.dynamicPortalUrl) {
      // URL Portail dynamique pour MAG/ENIGMA
      const portalUrl = dnsSettings.server_url ? `${dnsSettings.server_url}/c/` : 'http://DNS-SERVEUR:PORT/c/';
      return (
        <li key={index} className="flex items-start space-x-3">
          <span className="flex-shrink-0 w-6 h-6 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold">
            {index + 1}
          </span>
          <div className="text-[#E8E8E8] text-sm flex-1">
            {step.text}
            <div className="mt-2 bg-[#0A0A0B] border border-[#00FFFF] rounded-lg px-4 py-3 font-mono text-[#00FFFF] text-base font-bold flex items-center justify-between">
              <span className="break-all">{portalUrl}</span>
              <button
                onClick={() => copyToClipboard(portalUrl, `portal-${index}`)}
                className="ml-3 p-2 hover:bg-[#00FFFF]/20 rounded-lg transition-colors flex-shrink-0"
                title="Copier"
              >
                {copiedField === `portal-${index}` ? (
                  <Check className="h-4 w-4 text-[#00FF88]" />
                ) : (
                  <Copy className="h-4 w-4 text-[#00FFFF]" />
                )}
              </button>
            </div>
          </div>
        </li>
      );
    } else if (step.downloadsLink) {
      return (
        <li key={index} className="flex items-start space-x-3">
          <span className="flex-shrink-0 w-6 h-6 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold">
            {index + 1}
          </span>
          <div className="text-[#E8E8E8] text-sm flex-1">
            {step.text}
            <Link
              to="/telechargements"
              className="mt-2 inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#F7931E] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
              data-testid="tutorials-open-downloads"
            >
              <Download className="h-4 w-4" /> Ouvrir Téléchargements
            </Link>
          </div>
        </li>
      );
    } else if (step.link) {
      return (
        <li key={index} className="flex items-start space-x-3">
          <span className="flex-shrink-0 w-6 h-6 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold">
            {index + 1}
          </span>
          <div className="text-[#E8E8E8] text-sm">
            {step.text}
            <a 
              href={step.link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block mt-2 text-[#00FFFF] hover:underline font-mono"
            >
              {step.link}
            </a>
          </div>
        </li>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen py-20" data-testid="tutorials-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Tutoriels et assistance<br />
            <span className="text-[#FF6B35]">sur tous vos appareils</span>
          </h1>
          <p className="text-base sm:text-lg text-[#B8B8BA] max-w-3xl mx-auto">
            Trouvez un guide ou demandez directement de l'aide à Nova.
          </p>
        </motion.div>

        {/* Bloc d'accès Téléchargements (Phase 4.4-C) */}
        <div className="mb-12 rounded-2xl border border-[#FF6B35]/30 bg-gradient-to-r from-[#FF6B35]/10 to-transparent p-6 flex flex-col md:flex-row items-center justify-between gap-4" data-testid="tutorials-downloads-block">
          <div>
            <h3 className="text-white text-lg font-semibold flex items-center gap-2">
              <Download className="h-5 w-5 text-[#FF6B35]" /> Besoin d'installer une application ?
            </h3>
            <p className="text-[#B8B8BA] text-sm mt-1">
              Retrouvez dans l'onglet Téléchargements les applications disponibles et les codes à utiliser avec Downloader.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/telechargements" onClick={() => { sessionStorage.setItem('nova_downloads_source', 'tutoriels'); api.post('/assistant/page-event', { event: 'downloads_opened', source: 'tutoriels' }).catch(() => {}); }}
              className="inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#F7931E] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors" data-testid="tutorials-open-downloads-block">
              <Download className="h-4 w-4" /> Ouvrir Téléchargements
            </Link>
            <Link to="/assistance?intent=setup_iptv"
              className="inline-flex items-center gap-2 border border-[#3A3A3D] hover:border-[#FF6B35] text-[#E8E8E8] text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors" data-testid="tutorials-install-with-nova">
              <Play className="h-4 w-4" /> Installer avec Nova
            </Link>
          </div>
        </div>

        {/* Assistant Nova + recherche + tutoriels dynamiques */}
        <TutorialsAssistant />

        {/* Choisissez votre appareil */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Choisissez votre appareil
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {devices.map((device, index) => (
              <motion.div
                key={device.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <button
                  onClick={() => toggleDevice(device.id)}
                  className={`w-full bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-6 rounded-xl border transition-all duration-300 text-left ${
                    expandedDevice === device.id 
                      ? 'border-[#FF6B35] shadow-lg shadow-[#FF6B35]/20' 
                      : 'border-white/10 hover:border-[#FF6B35]/50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-4">
                      <div style={{ color: device.color }}>
                        {device.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-1">
                          {device.title}
                          {device.recommended && (
                            <span className="ml-2 text-xs bg-[#00FF88] text-black px-2 py-1 rounded-full">
                              Recommandé
                            </span>
                          )}
                        </h3>
                        <p className="text-[#B8B8BA] text-sm">{device.subtitle}</p>
                      </div>
                    </div>
                    {expandedDevice === device.id ? (
                      <ChevronUp className="h-5 w-5 text-[#FF6B35] flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-[#B8B8BA] flex-shrink-0" />
                    )}
                  </div>
                </button>

                {/* Contenu détaillé */}
                {expandedDevice === device.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 bg-[#1A1A1D] p-6 rounded-xl border border-[#FF6B35]/30"
                  >
                    <p className="text-[#B8B8BA] mb-6">{device.description}</p>

                    {/* App recommandée */}
                    {device.app && (
                      <div className="bg-[#222226] p-4 rounded-lg mb-6 border border-[#3A3A3D]">
                        <div className="flex items-center space-x-2 mb-2">
                          <Play className="h-4 w-4 text-[#FF6B35]" />
                          <span className="text-white font-semibold">Application recommandée</span>
                        </div>
                        <p className="text-[#FF6B35] font-bold text-lg">{device.app}</p>
                        {device.appNote && (
                          <p className="text-[#B8B8BA] text-sm mt-1">{device.appNote}</p>
                        )}
                      </div>
                    )}

                    {/* Important (pour IBO Player) */}
                    {device.important && (
                      <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 p-4 rounded-lg mb-6">
                        <p className="text-[#FFB800] font-semibold mb-2">ℹ️ Information importante</p>
                        <p className="text-white text-sm mb-2">{device.important.text}</p>
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="text-[#FF6B35] font-bold text-lg">{device.important.price}</span>
                          <span className="text-[#B8B8BA]">{device.important.details}</span>
                        </div>
                      </div>
                    )}

                    {/* Avantages */}
                    {device.advantages && (
                      <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 p-4 rounded-lg mb-6">
                        <p className="text-[#00FF88] font-semibold mb-3">✨ Avantages</p>
                        <ul className="space-y-2">
                          {device.advantages.map((adv, idx) => (
                            <li key={idx} className="flex items-center space-x-2 text-white text-sm">
                              <span className="text-[#00FF88]">✓</span>
                              <span>{adv}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Méthodes d'installation (pour Android) */}
                    {device.methods && device.methods.map((method, methodIdx) => (
                      <div key={methodIdx} className="mb-6">
                        <h4 className="text-white font-bold mb-4 text-lg">{method.title}</h4>
                        <div className="bg-[#222226] p-4 rounded-lg border border-[#3A3A3D]">
                          <h5 className="text-[#00FFFF] font-semibold mb-4">📋 Installation étape par étape</h5>
                          <ol className="space-y-4">
                            {method.steps.map((step, stepIndex) => renderStep(step, stepIndex))}
                          </ol>
                        </div>
                      </div>
                    ))}

                    {/* Sections multiples (pour MAG/ENIGMA) */}
                    {device.sections && device.sections.map((section, sectionIdx) => (
                      <div key={sectionIdx} className="mb-6">
                        <h4 className="text-white font-bold mb-4 text-lg">{section.title}</h4>
                        <div className="bg-[#222226] p-4 rounded-lg border border-[#3A3A3D]">
                          <ol className="space-y-4">
                            {section.steps.map((step, stepIndex) => renderStep(step, stepIndex))}
                          </ol>
                          {section.warning && (
                            <div className="mt-4 bg-[#FF3B5C]/10 border border-[#FF3B5C]/30 p-3 rounded-lg">
                              <p className="text-[#FF3B5C] text-sm font-semibold">{section.warning}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Étapes d'installation standard */}
                    {device.steps && !device.methods && !device.sections && (
                      <div className="mb-6">
                        <h4 className="text-white font-bold mb-4">📋 Installation étape par étape</h4>
                        <ol className="space-y-3">
                          {device.steps.map((step, stepIndex) => renderStep(step, stepIndex))}
                        </ol>
                      </div>
                    )}

                    {/* URL Serveur Smarters dynamique (pour Samsung/LG Smarters) */}
                    {device.dynamicServerUrl && (
                      <div className="bg-[#00FFFF]/10 border border-[#00FFFF]/30 p-4 rounded-lg mb-6">
                        <p className="text-[#00FFFF] font-semibold mb-2">🌐 URL Serveur Samsung / LG</p>
                        {dnsSettings.smarters_domain ? (
                          <div className="bg-[#0A0A0B] border border-[#00FFFF] rounded-lg px-4 py-3 flex items-center justify-between">
                            <code className="text-[#00FF88] font-mono text-sm break-all">{dnsSettings.smarters_domain}</code>
                            <button
                              onClick={() => copyToClipboard(dnsSettings.smarters_domain, 'smarters-url')}
                              className="ml-3 p-2 hover:bg-[#00FFFF]/20 rounded-lg transition-colors flex-shrink-0"
                              title="Copier"
                            >
                              {copiedField === 'smarters-url' ? (
                                <Check className="h-4 w-4 text-[#00FF88]" />
                              ) : (
                                <Copy className="h-4 w-4 text-[#00FFFF]" />
                              )}
                            </button>
                          </div>
                        ) : (
                          <p className="text-[#B8B8BA] text-sm">URL disponible dans votre espace client ou contactez le support.</p>
                        )}
                      </div>
                    )}

                    {/* Lien M3U dynamique (pour IBO Player) */}
                    {device.dynamicM3U && (
                      <div className="bg-[#222226] p-4 rounded-lg mb-6 border border-[#3A3A3D]">
                        <p className="text-[#FF6B35] font-semibold mb-2">🔐 Votre lien M3U</p>
                        {userCredentials && userCredentials.username ? (
                          <div className="space-y-3">
                            <p className="text-[#00FF88] text-sm">✓ Lien personnalisé avec vos identifiants :</p>
                            <div className="bg-[#0A0A0B] border border-[#00FF88] rounded-lg px-4 py-3 flex items-center justify-between">
                              <code className="text-[#00FF88] font-mono text-xs break-all flex-1">{generateM3ULink()}</code>
                              <button
                                onClick={() => copyToClipboard(generateM3ULink(), 'm3u-link')}
                                className="ml-3 p-2 hover:bg-[#00FF88]/20 rounded-lg transition-colors flex-shrink-0"
                                title="Copier"
                              >
                                {copiedField === 'm3u-link' ? (
                                  <Check className="h-4 w-4 text-[#00FF88]" />
                                ) : (
                                  <Copy className="h-4 w-4 text-[#00FF88]" />
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <p className="text-white text-sm">Format du lien M3U :</p>
                            <code className="block bg-[#0A0A0B] text-[#FFB800] p-3 rounded-lg text-xs font-mono break-all">
                              {generateM3ULink()}
                            </code>
                            <p className="text-[#B8B8BA] text-xs">
                              💡 <Link to="/connexion" className="text-[#00FFFF] hover:underline">Connectez-vous</Link> pour obtenir votre lien M3U personnalisé.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Alternative */}
                    {device.alternative && (
                      <div className="bg-[#3A3A3D]/30 p-4 rounded-lg mb-6 border border-[#3A3A3D]">
                        <p className="text-[#B8B8BA] text-sm">
                          📌 <strong>Alternative :</strong> {device.alternative}
                        </p>
                      </div>
                    )}

                    {/* Tutoriel vidéo */}
                    {device.videoAvailable && (
                      <div className="bg-[#222226] p-6 rounded-lg border border-[#FF6B35]/30 text-center">
                        <Play className="h-8 w-8 text-[#FF6B35] mx-auto mb-3" />
                        <p className="text-white font-semibold mb-1">🎬 Tutoriel vidéo</p>
                        <p className="text-[#B8B8BA] text-sm">Bientôt en ligne</p>
                      </div>
                    )}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Applications compatibles */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-16"
        >
          <div className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-8 rounded-xl border border-white/10">
            <h2 className="text-2xl font-bold text-white mb-4 text-center">
              Applications compatibles
            </h2>
            <p className="text-[#B8B8BA] text-center mb-6">
              Vos identifiants <span className="text-[#FF6B35] font-semibold">Xtream Codes API</span> sont compatibles avec la majorité des applications IPTV du marché.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {compatibleApps.map((app, index) => (
                <div
                  key={index}
                  className="bg-[#222226] p-4 rounded-lg border border-[#3A3A3D] text-center"
                >
                  <p className="text-white font-semibold text-sm mb-1">{app.name}</p>
                  <p className="text-[#6B6B6E] text-xs">{app.platform}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Rappel Important */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mb-16"
          data-testid="url-guide-section"
        >
          <div className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-8 rounded-xl border border-white/10">
            <h2 className="text-3xl font-bold text-white mb-2 text-center">
              Comprendre vos URLs
            </h2>
            <p className="text-[#B8B8BA] text-center mb-8">
              Vous avez re&ccedil;u plusieurs liens avec vos identifiants. Voici lequel utiliser selon votre appareil et votre application.
            </p>

            {/* Schéma visuel */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">

              {/* Xtream Codes */}
              <div className="bg-[#222226] p-6 rounded-xl border-2 border-[#00FFFF]/40 relative">
                <div className="absolute -top-3 left-4 bg-[#00FFFF] text-black text-xs font-bold px-3 py-1 rounded-full">
                  RECOMMAND&Eacute;
                </div>
                <div className="text-center mb-4 mt-2">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#00FFFF]/10 mb-3">
                    <Globe className="h-7 w-7 text-[#00FFFF]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#00FFFF]">Connexion Xtream Codes</h3>
                </div>
                <div className="bg-[#1A1A1D] p-3 rounded-lg mb-4">
                  <p className="text-[#E8E8E8] text-sm font-mono text-center">
                    <span className="text-[#00FFFF]">Username</span> + <span className="text-[#00FFFF]">Password</span> + <span className="text-[#00FFFF]">URL Serveur</span>
                  </p>
                </div>
                <p className="text-[#B8B8BA] text-sm mb-4">
                  La m&eacute;thode universelle. Fonctionne avec <strong className="text-white">toutes</strong> les applications compatibles Xtream Codes.
                </p>
                <div className="space-y-2">
                  <p className="text-[#E8E8E8] text-xs font-semibold mb-1">Applications compatibles :</p>
                  {['GOLDEN OTT', 'IPTV Smarters', 'TiviMate', 'XCIPTV', 'GSE IPTV', 'Purple IPTV'].map((app, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <div className="w-1 h-1 rounded-full bg-[#00FFFF]" />
                      <span className="text-[#E8E8E8] text-xs">{app}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-[#00FFFF]/5 rounded-lg border border-[#00FFFF]/20">
                  <p className="text-[#00FFFF] text-xs font-semibold">Android, iOS, Windows, Mac, Fire Stick, Box Android, Cl&eacute; HDMI</p>
                </div>
              </div>

              {/* Smarters Samsung/LG */}
              <div className="bg-[#222226] p-6 rounded-xl border border-[#FFB800]/30">
                <div className="text-center mb-4">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FFB800]/10 mb-3">
                    <Tv className="h-7 w-7 text-[#FFB800]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#FFB800]">URL Smarters</h3>
                </div>
                <div className="bg-[#1A1A1D] p-3 rounded-lg mb-4">
                  <p className="text-[#FFB800] text-sm font-mono text-center">
                    URL d&eacute;di&eacute;e Smarters
                  </p>
                </div>
                <p className="text-[#B8B8BA] text-sm mb-4">
                  Cette URL est <strong className="text-[#FFB800]">exclusivement r&eacute;serv&eacute;e</strong> &agrave; l'application IPTV Smarters install&eacute;e sur Smart TV.
                </p>
                <div className="space-y-2 mb-4">
                  <p className="text-[#E8E8E8] text-xs font-semibold mb-1">Appareils compatibles :</p>
                  {['Samsung Smart TV', 'LG Smart TV'].map((app, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <div className="w-1 h-1 rounded-full bg-[#FFB800]" />
                      <span className="text-[#E8E8E8] text-xs">{app}</span>
                    </div>
                  ))}
                </div>
                <div className="p-3 bg-[#FFB800]/5 rounded-lg border border-[#FFB800]/20">
                  <p className="text-[#FFB800] text-xs font-semibold">Samsung et LG uniquement — via app IPTV Smarters</p>
                </div>
              </div>

              {/* M3U */}
              <div className="bg-[#222226] p-6 rounded-xl border border-[#00FF88]/30">
                <div className="text-center mb-4">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#00FF88]/10 mb-3">
                    <Download className="h-7 w-7 text-[#00FF88]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#00FF88]">Lien M3U</h3>
                </div>
                <div className="bg-[#1A1A1D] p-3 rounded-lg mb-4">
                  <p className="text-[#00FF88] text-sm font-mono text-center">
                    URL de playlist en ligne
                  </p>
                </div>
                <p className="text-[#B8B8BA] text-sm mb-4">
                  Pour les applications qui injectent une playlist via un <strong className="text-white">portail web</strong>. Collez le lien dans le champ "URL playlist".
                </p>
                <div className="space-y-2 mb-4">
                  <p className="text-[#E8E8E8] text-xs font-semibold mb-1">Applications compatibles :</p>
                  {['IBO Player', 'Smart One', 'SET IPTV', 'Net IPTV', 'IPTV Extreme'].map((app, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <div className="w-1 h-1 rounded-full bg-[#00FF88]" />
                      <span className="text-[#E8E8E8] text-xs">{app}</span>
                    </div>
                  ))}
                </div>
                <div className="p-3 bg-[#00FF88]/5 rounded-lg border border-[#00FF88]/20">
                  <p className="text-[#00FF88] text-xs font-semibold">Samsung, LG, Android — via portail web de l'app</p>
                </div>
              </div>
            </div>

            {/* Résumé tableau */}
            <div className="bg-[#0A0A0B] p-6 rounded-xl border border-[#3A3A3D]">
              <h3 className="text-white font-bold mb-4 text-center">R&eacute;sum&eacute; rapide</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#3A3A3D]">
                      <th className="text-left py-3 px-4 text-[#B8B8BA] font-semibold">Situation</th>
                      <th className="text-left py-3 px-4 text-[#B8B8BA] font-semibold">URL &agrave; utiliser</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-[#3A3A3D]/50">
                      <td className="py-3 px-4 text-[#E8E8E8]">Android (TV, Box, Smartphone, Tablette)</td>
                      <td className="py-3 px-4"><span className="text-[#00FFFF] font-semibold">Xtream Codes</span></td>
                    </tr>
                    <tr className="border-b border-[#3A3A3D]/50">
                      <td className="py-3 px-4 text-[#E8E8E8]">Samsung / LG avec IPTV Smarters</td>
                      <td className="py-3 px-4"><span className="text-[#FFB800] font-semibold">URL Smarters</span></td>
                    </tr>
                    <tr className="border-b border-[#3A3A3D]/50">
                      <td className="py-3 px-4 text-[#E8E8E8]">Samsung / LG avec IBO Player, Smart One...</td>
                      <td className="py-3 px-4"><span className="text-[#00FF88] font-semibold">Lien M3U</span></td>
                    </tr>
                    <tr className="border-b border-[#3A3A3D]/50">
                      <td className="py-3 px-4 text-[#E8E8E8]">iOS (iPhone, iPad)</td>
                      <td className="py-3 px-4"><span className="text-[#00FFFF] font-semibold">Xtream Codes</span></td>
                    </tr>
                    <tr className="border-b border-[#3A3A3D]/50">
                      <td className="py-3 px-4 text-[#E8E8E8]">MAG / Enigma2</td>
                      <td className="py-3 px-4"><span className="text-[#00FFFF] font-semibold">URL Serveur + /c/</span></td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-[#E8E8E8]">PC (VLC, Kodi...)</td>
                      <td className="py-3 px-4"><span className="text-[#00FF88] font-semibold">Lien M3U</span> ou <span className="text-[#00FFFF] font-semibold">Xtream Codes</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Rappel Important - original */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-16"
        >
          <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 p-6 rounded-xl">
            <h3 className="text-xl font-bold text-[#FFB800] mb-4 flex items-center space-x-2">
              <AlertCircle className="h-6 w-6" />
              <span>🎯 Rappel Important</span>
            </h3>
            <ul className="space-y-2 text-white text-sm">
              <li className="flex items-start space-x-2">
                <span className="text-[#FFB800]">•</span>
                <span>Pour <strong>MAG / ENIGMA2</strong>, l'URL doit obligatoirement se terminer par <code className="bg-[#0A0A0B] px-2 py-0.5 rounded text-[#00FFFF]">/c/</code></span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#FFB800]">•</span>
                <span>Pour <strong>Samsung/LG avec IPTV Smarters</strong>, utilisez l'URL Smarters dédiée (disponible dans votre espace client)</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#FFB800]">•</span>
                <span>Vos identifiants sont disponibles dans votre <Link to="/client/dashboard" className="text-[#00FFFF] hover:underline">espace client</Link></span>
              </li>
            </ul>
          </div>
        </motion.div>

        {/* Message de rassurance */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-10 rounded-xl border border-white/10 text-center"
        >
          <h3 className="text-3xl font-bold text-white mb-4">
            Vous ne savez pas quoi choisir ?
          </h3>
          <p className="text-[#B8B8BA] text-lg mb-8">
            Nous vous guidons vers la solution la plus simple selon votre équipement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/client"
              className="bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-semibold px-8 py-3 rounded-lg hover:shadow-xl hover:shadow-[#FF6B35]/50 transition-all duration-300 inline-block"
            >
              Accéder à mon espace
            </Link>
            <Link
              to="/telechargements"
              className="bg-[#00FF88] text-black font-semibold px-8 py-3 rounded-lg hover:bg-[#00E57A] transition-all duration-300 inline-block"
            >
              Téléchargements
            </Link>
            <Link
              to="/faq"
              className="bg-[#222226] text-white font-semibold px-8 py-3 rounded-lg border border-[#3A3A3D] hover:border-[#FF6B35] transition-all duration-300 inline-block"
            >
              Consulter la FAQ
            </Link>
          </div>

          {/* Support info */}
          <div className="mt-8 pt-8 border-t border-[#3A3A3D]">
            <p className="text-[#B8B8BA] mb-4">
              📞 Notre équipe support est là pour vous accompagner 24/7
            </p>
            <p className="text-[#6B6B6E] text-sm">
              Support technique • Installation guidée • Aide personnalisée
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Tutorials;
